import { observer } from 'mobx-react';
import { useState } from 'react';
import { Maybe, just, nothing } from 'maybeasy';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { T } from '../../../../../../../../../Translations';
import { ClientExperienceResource } from '../../../../Types';
import { trackIntercomEvent } from '../../../../../../../../../services/intercomTrackingService';
import { QuickAddGroup, quickAddGroups } from './Groups';
import QuickAddButton from './QuickAddButton';

interface Props {
  experiences: ClientExperienceResource[];
  addedCourseIds: number[];
  onAddCourses: (courseIds: number[]) => void;
  onRemoveCourses: (courseIds: number[]) => void;
}

function QuickAddCourses({ experiences, addedCourseIds, onAddCourses, onRemoveCourses }: Props) {
  const [isOpen, setIsOpen] = useState(true);
  const [justClicked, setJustClicked] = useState<Maybe<QuickAddGroup['key']>>(nothing());

  const groups = quickAddGroups(experiences);
  if (groups.length === 0) {
    return <></>;
  }

  const handleToggleOpen = () => {
    trackIntercomEvent('Builder V2 - Course Catalog - Toggle Quick Add', {
      action: isOpen ? 'collapse' : 'expand',
    });
    setIsOpen(!isOpen);
  };

  const handleClick = (group: QuickAddGroup, notYetAdded: number[]) => {
    const isDone = notYetAdded.length === 0;
    const courseIds = isDone ? group.courseIds : notYetAdded;
    trackIntercomEvent('Builder V2 - Course Catalog - Quick Add Courses', {
      courseType: group.key,
      action: isDone ? 'remove' : 'add',
      courseCount: courseIds.length,
    });
    setJustClicked(just(group.key));
    if (isDone) {
      onRemoveCourses(courseIds);
    } else {
      onAddCourses(courseIds);
    }
  };

  // Pointer leaving or focus moving away both end the just-clicked state
  const handleLeave = (group: QuickAddGroup) => {
    if (justClicked.map((key) => key === group.key).getOrElseValue(false)) {
      setJustClicked(nothing());
    }
  };

  return (
    <div
      className={`rounded-lg bg-[color-mix(in_srgb,var(--primary)_8%,var(--card))] px-3 ${isOpen ? 'py-3 space-y-2' : 'py-2'}`}
      data-intercom-target="builder-v2-quick-add-section"
    >
      <button
        type="button"
        onClick={handleToggleOpen}
        className="w-full flex items-center justify-between uppercase tracking-wide text-primary"
        style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)' }}
        aria-expanded={isOpen}
        data-intercom-target="builder-v2-quick-add-toggle"
      >
        <span>
          <T kind="Quick add courses" />
        </span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
      </button>

      {isOpen && (
        <div className="flex flex-wrap gap-2">
          {groups.map((group) => {
            const notYetAdded = group.courseIds.filter((id) => !addedCourseIds.includes(id));
            return (
              <QuickAddButton
                key={group.key}
                group={group}
                isDone={notYetAdded.length === 0}
                hoverable={justClicked.map((key) => key !== group.key).getOrElseValue(true)}
                onClick={() => handleClick(group, notYetAdded)}
                onLeave={() => handleLeave(group)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default observer(QuickAddCourses);
